/* ==========================================================================
   Homepage Comparison - BB2  (shared by variants A and B)
     sections/homepage-comparison-a-bb2.liquid
     sections/homepage-comparison-b-bb2.liquid

   Two jobs:
     1. Scroll affordances — fade the right edge and show the swipe hint only
        while there is more table to reveal. Both variants.
     2. Progressive reveal — chips add or remove optional columns, recomputing
        the column widths so two, three or four columns all fill the row.
        Variant B only; a table with no chips simply skips it.

   Loaded with `defer`, so the definition may register before or after the
   markup exists — custom elements upgrade either way, which is also what keeps
   this working when the theme editor re-renders the section.
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('hp-comparison-bb2')) return;

  var STACKED = window.matchMedia('(max-width: 639px)');

  customElements.define(
    'hp-comparison-bb2',
    class HpComparisonBB2 extends HTMLElement {
      connectedCallback() {
        this.table = this.querySelector('[data-cmp-table]');
        this.scroller = this.querySelector('[data-cmp-scroll]');
        if (!this.table || !this.scroller) return;

        this.fade = this.querySelector('[data-cmp-fade]');
        this.hint = this.querySelector('[data-cmp-hint]');
        this.swipePrev = this.querySelector('[data-cmp-swipe="prev"]');
        this.swipeNext = this.querySelector('[data-cmp-swipe="next"]');
        this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-cmp-toggle]'));
        this.labelCol = this.table.querySelector('col:not([data-colw])');
        this.valueCols = Array.prototype.slice.call(this.table.querySelectorAll('col[data-colw]'));
        this.labelPct = parseFloat(this.dataset.labelWidth) || 23;

        var self = this;

        // Columns that are always on: every one that is not behind a chip.
        var optional = this.chips.map(function (chip) { return chip.dataset.cmpToggle; });
        this.always = this.valueCols
          .map(function (col) { return col.dataset.colw; })
          .filter(function (key) { return optional.indexOf(key) === -1; });

        this.state = {};
        optional.forEach(function (key) { self.state[key] = false; });

        this.onScroll = function () { self.updateAffordances(); };
        this.onResize = function () { self.render(); self.positionSwipe(); };
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        // One column per press, so the neighbouring product comes into view
        // rather than jumping to either end of the table.
        if (this.swipePrev) {
          this.swipePrev.addEventListener('click', function () { self.stepColumn(-1); });
        }
        if (this.swipeNext) {
          this.swipeNext.addEventListener('click', function () { self.stepColumn(1); });
        }

        this.chips.forEach(function (chip) {
          chip.addEventListener('click', function () {
            var key = chip.dataset.cmpToggle;
            self.state[key] = !self.state[key];
            self.render();
          });
        });

        // First render after a frame, so the scroller has a real width to measure.
        if (window.requestAnimationFrame) {
          requestAnimationFrame(function () { self.render(); self.positionSwipe(); });
        } else {
          this.render();
        }
      }

      disconnectedCallback() {
        if (this.scroller && this.onScroll) {
          this.scroller.removeEventListener('scroll', this.onScroll);
        }
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      visibleKeys() {
        var self = this;
        return this.valueCols
          .map(function (col) { return col.dataset.colw; })
          .filter(function (key) {
            return self.always.indexOf(key) !== -1 || self.state[key];
          });
      }

      // Min table width per visible column count. Two columns need no minimum, so
      // the compressed variant's default state fits a phone with no sideways
      // scrolling at all — which is the entire point of it. An empty string falls
      // back to the width the stylesheet sets.
      minWidthFor(count) {
        if (count <= 2) return '0px';
        if (count === 3) return '660px';
        return '';
      }

      render() {
        if (!this.chips.length) {
          // Variant A: fixed columns, nothing to recompute.
          this.updateAffordances();
          return;
        }

        var visible = this.visibleKeys();
        var count = visible.length || 1;

        // In the stacked mobile layout there are no columns to keep wide.
        this.table.style.minWidth = STACKED.matches ? '0px' : this.minWidthFor(count);

        // Give the row labels more of the row once the table is squeezed narrow.
        // A width of 0 means layout has not settled yet — keep the configured
        // share rather than briefly widening the labels on first paint.
        var scrollerWidth = this.scroller.clientWidth;
        var labelPct = scrollerWidth > 0 && scrollerWidth < 560 ? 32 : this.labelPct;
        if (this.labelCol) this.labelCol.style.width = labelPct + '%';
        var width = ((100 - labelPct) / count).toFixed(3) + '%';

        // Widths are POSITIONAL, not per-column. Hiding a cell with display:none
        // removes it from its row, so the cells that remain fill the first N
        // slots in order — the width has to follow the slot, not the column it
        // originally belonged to.
        this.valueCols.forEach(function (col, i) {
          col.style.width = i < count ? width : '0';
        });

        var self = this;
        Array.prototype.forEach.call(this.table.querySelectorAll('[data-col]'), function (cell) {
          var on = visible.indexOf(cell.dataset.col) !== -1;
          cell.style.display = on ? '' : 'none';
          cell.style.opacity = on ? '1' : '0';
        });

        this.chips.forEach(function (chip) {
          var on = !!self.state[chip.dataset.cmpToggle];
          chip.setAttribute('aria-checked', on ? 'true' : 'false');
          var icon = chip.querySelector('[data-cmp-chip-icon]');
          if (icon) icon.textContent = on ? '✓' : '+';
        });

        this.updateAffordances();
      }

      // Line the chevrons up with the column-title row. Recomputed on resize,
      // since that row's height changes with the viewport.
      positionSwipe() {
        if (!this.swipePrev && !this.swipeNext) return;
        var titleRow = this.table.querySelector('thead tr:last-child');
        var wrap = this.scroller.parentElement;
        if (!titleRow || !wrap) return;

        var row = titleRow.getBoundingClientRect();
        var box = wrap.getBoundingClientRect();
        var top = row.top + row.height / 2 - box.top + 'px';
        if (this.swipePrev) this.swipePrev.style.top = top;
        if (this.swipeNext) this.swipeNext.style.top = top;
      }

      stepColumn(direction) {
        var cell = this.table.querySelector('thead tr:last-child [data-col]');
        var width = cell ? cell.getBoundingClientRect().width : this.scroller.clientWidth * 0.8;
        this.scroller.scrollBy({ left: direction * width, behavior: 'smooth' });
      }

      updateAffordances() {
        var max = this.scroller.scrollWidth - this.scroller.clientWidth;
        var more = max - this.scroller.scrollLeft > 4;
        var back = this.scroller.scrollLeft > 4;
        if (this.fade) this.fade.style.opacity = more ? '1' : '0';
        if (this.hint) this.hint.hidden = max <= 4;

        // Faded out rather than hidden, so the row does not reflow at the ends.
        if (this.swipeNext) {
          this.swipeNext.style.opacity = more ? '1' : '0';
          this.swipeNext.style.pointerEvents = more ? 'auto' : 'none';
        }
        if (this.swipePrev) {
          this.swipePrev.style.opacity = back ? '1' : '0';
          this.swipePrev.style.pointerEvents = back ? 'auto' : 'none';
        }
      }
    }
  );
})();
